import { FieldValues, Path, PathValue } from 'react-hook-form';
import {
  useRadioGroupFieldController,
  UseRadioGroupFieldControllerProps,
} from '../../../hooks/form/useRadioGroupFieldController';
import ErrorMessage from '../shared/ErrorMessage';
import RadioGroup, { RadioGroupProps } from '../../library/RadioGroup';

type RadioGroupPropsWithControllerProps<TFieldValues> = Omit<
  RadioGroupProps,
  | 'value'
  | 'name'
  | 'defaultValue'
  | 'disabled'
  | 'onChange'
  | 'required'
  | 'error'
> &
  UseRadioGroupFieldControllerProps<TFieldValues>;

export interface RadioGroupFieldProps<TFieldValues>
  extends RadioGroupPropsWithControllerProps<TFieldValues> {}

export default function RadioGroupField<TFieldValues extends FieldValues>(
  props: RadioGroupFieldProps<TFieldValues>,
) {
  const {
    name,
    label,
    helperText,
    rules,
    disabled,
    shouldUnregister,
    onChange,
    children,
    fullWidth = true,
    defaultValue = '',
    ...restRadioGroupProps
  } = props;

  const {
    field,
    required,
    formState: { errors },
    fieldState: { invalid: error },
    onChangeField,
  } = useRadioGroupFieldController({
    name,
    defaultValue: defaultValue as PathValue<TFieldValues, Path<TFieldValues>>,
    rules,
    disabled,
    shouldUnregister,
    onChange,
  });

  return (
    <RadioGroup
      name={field.name}
      value={field.value}
      ref={field.ref}
      onChange={onChangeField}
      required={required}
      error={error}
      errorMessage={<ErrorMessage name={field.name} errors={errors} />}
      fullWidth={fullWidth}
      label={label}
      helperText={helperText}
      {...restRadioGroupProps}
    >
      {children}
    </RadioGroup>
  );
}
